/**
 * Customer Advisory Page - Send newsletters/advisories to customers
 * Filter by service type (LCL, FCL, Air) and country (origin/destination)
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  Search,
  Mail,
  Send,
  Users,
  Ship,
  Plane,
  Truck,
  Package,
  AlertTriangle,
  TrendingUp,
  FileText,
  RefreshCw,
  CheckSquare,
  X,
  Building2,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";


const SERVICE_TYPES = {
  lcl: { label: "LCL", icon: Package, color: "bg-blue-500" },
  fcl: { label: "FCL", icon: Ship, color: "bg-green-500" },
  air: { label: "Air Freight", icon: Plane, color: "bg-purple-500" },
  inland: { label: "Inland", icon: Truck, color: "bg-orange-500" },
};

const ADVISORY_TEMPLATES = [
  { id: "disruption", label: "Service Disruption", icon: AlertTriangle, color: "text-red-600",
    subject: "Service Advisory - [REGION] Operations Update",
    body: `Dear Valued Customer,

We would like to inform you about the current situation affecting [SERVICE] shipments to/from [REGION].

[DETAILS]

We are actively monitoring the situation and will keep you informed.

Best regards,
Europacific Team` },
  { id: "rate_increase", label: "Rate Increase", icon: TrendingUp, color: "text-orange-600",
    subject: "Rate Adjustment Notice - [SERVICE] Services",
    body: `Dear Valued Customer,

We would like to inform you about upcoming rate adjustments affecting [SERVICE] services.

Effective Date: [DATE]
[DETAILS]

Best regards,
Europacific Team` },
  { id: "surcharge", label: "Surcharge Notice", icon: FileText, color: "text-blue-600",
    subject: "Surcharge Advisory - [REGION]",
    body: `Dear Valued Customer,

Please be advised of the following surcharge applicable to shipments to/from [REGION]:

[DETAILS]

This surcharge will be effective from [DATE].

Best regards,
Europacific Team` },
  { id: "custom", label: "Custom Message", icon: Mail, color: "text-slate-600", subject: "", body: "" },
];

export default function CustomerAdvisoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  const [serviceFilter, setServiceFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [countryType, setCountryType] = useState("any");
  const [searchQuery, setSearchQuery] = useState("");
  const [availableCountries, setAvailableCountries] = useState([]);
  
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [bccEmails, setBccEmails] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Attachments — drag-drop OR file picker (added 2026-05-27 by Rok's
  // request). Mirrors the Partner Directory bulk-email uploader.
  // Limits: 10 MB / file, 25 MB total — enforced UI-side AND backend-side.
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const MAX_FILE_MB = 10;
  const MAX_TOTAL_MB = 25;
  const _fileToBase64 = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const dataUrl = fr.result || "";
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
  const addEmailAttachments = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than ${MAX_FILE_MB} MB.`);
      return;
    }
    const currentTotal = emailAttachments.reduce((s, a) => s + (a.size || 0), 0);
    const addedTotal = files.reduce((s, f) => s + f.size, 0);
    if (currentTotal + addedTotal > MAX_TOTAL_MB * 1024 * 1024) {
      toast.error(`Total attachments would exceed ${MAX_TOTAL_MB} MB.`);
      return;
    }
    const newRows = [];
    for (const f of files) {
      try {
        const content = await _fileToBase64(f);
        newRows.push({
          name: f.name,
          size: f.size,
          type: f.type || "application/octet-stream",
          content,
        });
      } catch {
        toast.error(`Failed to read "${f.name}"`);
      }
    }
    setEmailAttachments((prev) => [...prev, ...newRows]);
  };
  const removeEmailAttachment = (idx) =>
    setEmailAttachments((prev) => prev.filter((_, i) => i !== idx));
  const onAttachmentsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) addEmailAttachments(e.dataTransfer.files);
  };

  const [stats, setStats] = useState({ total: 0, byService: {} });

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/customer-advisory/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setCustomers(data.customers);
      setFilteredCustomers(data.customers);
      setAvailableCountries(data.countries);
      setStats(data.stats);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    let filtered = [...customers];
    if (serviceFilter && serviceFilter !== "all") filtered = filtered.filter(c => c.service_type === serviceFilter);
    if (sourceFilter && sourceFilter !== "all") filtered = filtered.filter(c => c.source === sourceFilter);
    if (countryFilter && countryFilter !== "all") {
      filtered = filtered.filter(c => {
        if (countryType === "origin") return c.origin_country === countryFilter;
        if (countryType === "destination") return c.destination_country === countryFilter;
        return c.origin_country === countryFilter || c.destination_country === countryFilter;
      });
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(c =>
        c.company_name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.contact_name?.toLowerCase().includes(q) ||
        c.origin_country?.toLowerCase().includes(q) ||
        c.destination_country?.toLowerCase().includes(q) ||
        c.origin_city?.toLowerCase().includes(q) ||
        c.destination_city?.toLowerCase().includes(q) ||
        c.offer_number?.toLowerCase().includes(q)
      );
    }
    setFilteredCustomers(filtered);
    setSelectedCustomers(new Set());
    setSelectAll(false);
  }, [customers, serviceFilter, sourceFilter, countryFilter, countryType, searchQuery]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedCustomers(checked ? new Set(filteredCustomers.map(c => c.id)) : new Set());
  };

  const handleSelectCustomer = (id, checked) => {
    const newSet = new Set(selectedCustomers);
    checked ? newSet.add(id) : newSet.delete(id);
    setSelectedCustomers(newSet);
    setSelectAll(newSet.size === filteredCustomers.length);
  };

  const openEmailModal = (template) => {
    setSelectedTemplate(template);
    setEmailSubject(template.subject);
    setEmailBody(template.body);
    setShowEmailModal(true);
  };

  const handleSendAdvisory = async () => {
    if (selectedCustomers.size === 0) { toast.error("Select customers"); return; }
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error("Fill subject and message"); return; }
    try {
      setSendingEmail(true);
      const token = localStorage.getItem("token");
      const ccList = ccEmails.split(/[,;]/).map(e => e.trim()).filter(e => e.includes("@"));
      const bccList = bccEmails.split(/[,;]/).map(e => e.trim()).filter(e => e.includes("@"));
      const res = await fetch(`${API_URL}/customer-advisory/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_ids: Array.from(selectedCustomers),
          subject: emailSubject, body: emailBody,
          staff_email: user?.email || "", staff_name: user?.full_name || "",
          cc_emails: ccList, bcc_emails: bccList, advisory_type: selectedTemplate?.id || "custom",
          attachments: emailAttachments.map((a) => ({
            filename: a.name,
            content: a.content,
            content_type: a.type,
          })),
        }),
      });
      // Read the body defensively. Some browser extensions / security tools
      // wrap window.fetch and consume the Response body before our handler
      // runs, which makes res.clone()/res.text() throw "Response body is
      // already used" — that previously surfaced as a fake send failure even
      // though the server had already sent the advisory. res.ok / res.status
      // are always available regardless of the body, so we rely on those for
      // success and only use the body for extra detail when it's readable.
      let payload = {};
      try {
        const rawText = await res.text();
        payload = rawText ? JSON.parse(rawText) : {};
      } catch (_) {
        payload = {};
      }
      if (!res.ok) throw new Error(payload.detail || payload.error || `HTTP ${res.status}`);
      toast.success(`Sent to ${payload.recipients_count ?? selectedCustomers.size} customers`);
      setShowEmailModal(false);
      setEmailSubject(""); setEmailBody(""); setCcEmails(""); setBccEmails(""); setEmailAttachments([]);
      setSelectedCustomers(new Set()); setSelectAll(false);
    } catch (err) { toast.error(err.message); }
    finally { setSendingEmail(false); }
  };

  const getUniqueEmailCount = () => {
    const emails = new Set();
    filteredCustomers.filter(c => selectedCustomers.has(c.id)).forEach(c => { if (c.email) emails.add(c.email); });
    return emails.size;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Navbar />
      <div className="h-14"></div>
      
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/offers")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Offers
          </Button>
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Mail className="h-6 w-6 text-blue-600" />
              Customer Advisory
            </h1>
            <p className="text-muted-foreground mt-1">
              Send service updates and advisories to customers from offers, quotes, and email archive
            </p>
          </div>
          <Button onClick={fetchCustomers} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2"><CardDescription>Total Customers</CardDescription></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" /> {stats.total}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>Email Archive</CardDescription></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-500" /> {stats.bySource?.email_archive || 0}
              </div>
            </CardContent>
          </Card>
          {Object.entries(SERVICE_TYPES).map(([key, cfg]) => (
            <Card key={key}>
              <CardHeader className="pb-2"><CardDescription>{cfg.label}</CardDescription></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold flex items-center gap-2">
                  <cfg.icon className="h-5 w-5 text-slate-500" /> {stats.byService?.[key] || 0}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-40">
                <Label className="text-xs">Source</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="All Sources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="email_archive">Email Archive</SelectItem>
                    <SelectItem value="crm">CRM Contacts</SelectItem>
                    <SelectItem value="partner_network">Partner Networks</SelectItem>
                    <SelectItem value="offer">Offers</SelectItem>
                    <SelectItem value="quote_request">Quote Requests</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs">Service Type</Label>
                <Select value={serviceFilter} onValueChange={setServiceFilter}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="All Services" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {Object.entries(SERVICE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Label className="text-xs">Country In</Label>
                <Select value={countryType} onValueChange={setCountryType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Origin or Dest</SelectItem>
                    <SelectItem value="origin">Origin Only</SelectItem>
                    <SelectItem value="destination">Dest Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label className="text-xs">Country</Label>
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="All Countries" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {availableCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Company, email, country, city…" className="pl-9" data-testid="advisory-search-input" />
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setServiceFilter("all"); setSourceFilter("all"); setCountryFilter("all"); setCountryType("any"); setSearchQuery(""); }}>
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Templates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {ADVISORY_TEMPLATES.map((t) => (
            <Button key={t.id} variant="outline" className={`h-auto py-4 flex flex-col gap-2 ${selectedCustomers.size === 0 ? "opacity-50" : ""}`}
              onClick={() => openEmailModal(t)} disabled={selectedCustomers.size === 0}>
              <t.icon className={`h-6 w-6 ${t.color}`} />
              <span className="text-sm">{t.label}</span>
            </Button>
          ))}
        </div>

        {/* Selection Bar */}
        {selectedCustomers.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-700">
              <CheckSquare className="h-5 w-5" />
              <strong>{selectedCustomers.size}</strong> selected ({getUniqueEmailCount()} emails)
            </div>
            <Button size="sm" onClick={() => openEmailModal(ADVISORY_TEMPLATES[3])}>
              <Send className="h-4 w-4 mr-1" /> Send Advisory
            </Button>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white border-b">
                  <tr>
                    <th className="p-3 w-12"><Checkbox checked={selectAll} onCheckedChange={handleSelectAll} /></th>
                    <th className="p-3 text-left text-sm font-medium">Company</th>
                    <th className="p-3 text-left text-sm font-medium">Contact / Email</th>
                    <th className="p-3 text-left text-sm font-medium">Source</th>
                    <th className="p-3 text-left text-sm font-medium">Service</th>
                    <th className="p-3 text-left text-sm font-medium">Route</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
                  ) : filteredCustomers.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No customers found</td></tr>
                  ) : filteredCustomers.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-slate-50">
                      <td className="p-3"><Checkbox checked={selectedCustomers.has(c.id)} onCheckedChange={(ch) => handleSelectCustomer(c.id, ch)} /></td>
                      <td className="p-3"><div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{c.company_name || "-"}</span></div></td>
                      <td className="p-3"><div className="text-sm"><div>{c.contact_name || "-"}</div><div className="text-blue-600">{c.email || "-"}</div></div></td>
                      <td className="p-3">
                        {c.source === "email_archive" ? (
                          <Badge className="bg-indigo-500 text-white text-xs">Archive</Badge>
                        ) : c.source === "offer" ? (
                          <Badge className="bg-green-500 text-white text-xs">Offer</Badge>
                        ) : c.source === "quote_request" ? (
                          <Badge className="bg-amber-500 text-white text-xs">Quote</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{c.source || "-"}</Badge>
                        )}
                      </td>
                      <td className="p-3">{c.service_type && SERVICE_TYPES[c.service_type] ? <Badge className={`${SERVICE_TYPES[c.service_type].color} text-white`}>{SERVICE_TYPES[c.service_type].label}</Badge> : "-"}</td>
                      <td className="p-3"><div className="text-sm flex items-center gap-1"><MapPin className="h-3 w-3 text-green-500" />{c.origin_city || c.origin_country || "-"}<span className="mx-1">→</span><MapPin className="h-3 w-3 text-red-500" />{c.destination_city || c.destination_country || "-"}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t text-sm text-muted-foreground">
              Showing {filteredCustomers.length} of {customers.length} customers
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Send Customer Advisory</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm bg-slate-50 p-3 rounded">
              Sending to <strong>{selectedCustomers.size}</strong> customers ({getUniqueEmailCount()} emails) via BCC. 
              Replies go to <span className="text-blue-600">{user?.email}</span>
            </div>
            <div><Label>Subject</Label><Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="mt-1" /></div>
            <div><Label>Message</Label><Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={10} className="mt-1 font-mono text-sm" /></div>
            <div><Label>CC (Optional)</Label><Input value={ccEmails} onChange={(e) => setCcEmails(e.target.value)} placeholder="colleague@company.com" className="mt-1" data-testid="advisory-cc-input" /></div>
            <div><Label>BCC (Optional)</Label><Input value={bccEmails} onChange={(e) => setBccEmails(e.target.value)} placeholder="manager@company.com" className="mt-1" data-testid="advisory-bcc-input" /><div className="text-[10px] text-slate-500 mt-1">Added to the blind-copy list alongside the customer recipients. Separate multiple with commas.</div></div>
            {/* Attachments — drag/drop OR click. 10 MB/file, 25 MB total. */}
            <div>
              <Label>Attachments (optional)</Label>
              <div className="text-xs text-slate-500 mb-1">
                Drag PDFs, schedules, price-change docs here (max 10 MB per file, 25 MB total). Same files go to every recipient.
              </div>
              <label
                htmlFor="advisory-files"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                onDrop={onAttachmentsDrop}
                className={`block cursor-pointer rounded border-2 border-dashed p-4 text-center transition-colors ${
                  isDragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400 bg-slate-50/50"
                }`}
                data-testid="advisory-attach-dropzone"
              >
                <input
                  id="advisory-files"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addEmailAttachments(e.target.files); e.target.value = ""; }}
                  data-testid="advisory-attach-input"
                />
                <div className="text-sm text-slate-700">
                  {isDragOver
                    ? "Release to attach"
                    : <>📎 <span className="underline text-blue-600">Click to choose files</span> or drag &amp; drop them here</>
                  }
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {emailAttachments.length > 0
                    ? `${emailAttachments.length} file${emailAttachments.length === 1 ? "" : "s"} attached`
                    : "No files attached yet"}
                </div>
              </label>
              {emailAttachments.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="advisory-attach-list">
                  {emailAttachments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1 text-xs">
                      <span className="flex-1 truncate text-slate-800" title={a.name}>📄 {a.name}</span>
                      <span className="text-slate-500 shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => removeEmailAttachment(idx)}
                        className="text-red-600 hover:text-red-700 shrink-0"
                        title="Remove"
                        data-testid={`advisory-attach-remove-${idx}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="text-[10px] text-slate-500 text-right">
                    Total: {(emailAttachments.reduce((s, a) => s + a.size, 0) / (1024 * 1024)).toFixed(2)} MB / {MAX_TOTAL_MB} MB
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs bg-slate-50 p-3 rounded">
              From: roket@europacific.com | Reply-To: {user?.email} | CC: {ccEmails || "(none)"} | BCC: {bccEmails || "(none)"}<br />
              Recipients in BCC (cannot see each other)
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button onClick={handleSendAdvisory} disabled={sendingEmail}>
              <Send className="h-4 w-4 mr-2" /> {sendingEmail ? "Sending..." : "Send Advisory"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
